/**
 * Graph Conversion — converts between BehaviorGraph spec format and ReactFlow nodes/edges.
 * Supports multi-graph mode with TRACE_subgraph declarations.
 */

import type { Node, Edge } from 'reactflow';
import { GraphOps, MACRO_ID_KEY, MACRO_OP_PREFIX, raiseMacros } from 'trace-viewer/behaviors';
import type { GraphData, GraphEvent, GraphNode, Declaration, GraphVariable } from 'trace-viewer/behaviors';
import type { SceneObject } from 'trace-viewer/scene';
import { getNodeDef } from './node-definitions';
import dagre, { type GraphLabel, type NodeLabel, type EdgeLabel } from '@dagrejs/dagre';

// ============================================================================
// Type signatures — all KHR_interactivity types + TRACE_interactivity extensions
// ============================================================================

/** Canonical ordered list of type signatures. Index in this array = type index in GraphData.types[]. */
export const ALL_TYPE_SIGNATURES = [
    'bool', 'int', 'float', 'float2', 'float3', 'float4',
    'float2x2', 'float3x3', 'float4x4', 'string', 'color',
] as const;

/** Number of scalar components for each type signature. */
export const TYPE_COMPONENT_COUNT: Record<string, number> = {
    bool: 1, int: 1, float: 1, string: 1, color: 1,
    float2: 2, float3: 3, float4: 4,
    float2x2: 4, float3x3: 9, float4x4: 16,
};

/**
 * Remap variable type indices from a graph's own types[] to canonical ALL_TYPE_SIGNATURES indices.
 * Call this when loading a graph so the editor always works with canonical type indices.
 */
export function remapVariablesToCanonical(variables: GraphVariable[], graphTypes: { signature: string }[]): GraphVariable[] {
    return variables.map(v => {
        const sig = graphTypes[v.type]?.signature ?? 'float';
        const canonicalIndex = ALL_TYPE_SIGNATURES.indexOf(sig as typeof ALL_TYPE_SIGNATURES[number]);
        return { ...v, type: canonicalIndex >= 0 ? canonicalIndex : 2 /* float */ };
    });
}

// ============================================================================
// ReactFlow node data shape
// ============================================================================

export interface FlowNodeData {
    op: string;
    label: string;
    /** Node index in the graph's nodes array (set on save, not during editing) */
    graphIndex?: number;
    /** Static configuration values (target paths, clip names, etc.) */
    config: Record<string, unknown>;
    /** Inline literal values for unconnected inputs */
    inlineValues: Record<string, unknown[]>;
    /**
     * For a macro block (`macro/…` op): the instance's shared id, from the
     * raised pseudo-node's metadata. Re-emitted on save so commit-time
     * lowering keeps the stored id stable across round-trips.
     */
    macroId?: string;
    /**
     * Dynamically discovered sockets. For ops without a NodeDef (e.g. KHR ops
     * like pointer/set) these are the only sockets; for known ops they carry
     * EXTRA flow sockets the data uses beyond the static def (e.g. a Sequence
     * wired past its default two outputs — degraded macro expansions do this)
     * and the node component merges them with the def's.
     */
    dynamicSockets?: {
        flowInputs: string[];
        flowOutputs: string[];
        dataInputs: string[];
        dataOutputs: string[];
    };
    /**
     * When this node is a subgraph invocation: the index into the multi-graph
     * `graphs[]` array that this node's declaration references.
     */
    subgraphIndex?: number;
    /**
     * Human-readable name of the subgraph (from the op string on the declaration).
     * Used as the label for subgraph nodes.
     */
    subgraphName?: string;
}

// ============================================================================
// Multi-graph container — used by the editor to track all graphs
// ============================================================================

export interface MultiGraphState {
    /** All graph definitions */
    graphs: GraphData[];
    /** Index of the currently-edited graph */
    editingIndex: number;
    /** Variables for the main (active) graph */
    variables: GraphVariable[];
}

/** Configuration key of `event/send` / `event/receive` naming the event index. */
const MACRO_EVENT_CONFIG = 'event';

// ============================================================================
// Spec → ReactFlow
// ============================================================================

/**
 * Render a graph for the editor, in its RAISED form: each recognized macro
 * expansion is one `macro/…` block (trace-viewer `raiseMacros`), so the block
 * is just another node here and edges address it by input/output name. A
 * group another tool edited is not raised and renders as plain nodes.
 *
 * Editor state must be raised as a whole — nodes AND variables, events and
 * interface sockets — because raising drops each block's private event and
 * variable and re-indexes the rest. BehaviorGraphPanel raises the behavior's
 * data on load; raising here as well is a no-op for that input and keeps other
 * callers (templates, tests) seeing blocks.
 */
export function graphToReactFlow(
    stored: GraphData,
    allGraphs?: GraphData[],
): { nodes: Node<FlowNodeData>[]; edges: Edge[] } {
    const graph = raiseMacros(stored);
    const nodes: Node<FlowNodeData>[] = [];
    const edges: Edge[] = [];

    // Pre-pass: collect data output sockets and flow input sockets referenced
    // by other nodes. These can't be discovered from the node's own data.
    const referencedDataOutputs = new Map<number, Set<string>>(); // nodeIndex → socket names
    const referencedFlowInputs = new Map<number, Set<string>>();  // nodeIndex → socket names
    for (const gNode of graph.nodes) {
        if (gNode.values) {
            for (const vi of Object.values(gNode.values)) {
                if ('node' in vi) {
                    let set = referencedDataOutputs.get(vi.node);
                    if (!set) { set = new Set(); referencedDataOutputs.set(vi.node, set); }
                    set.add(vi.socket);
                }
            }
        }
        if (gNode.flows) {
            for (const target of Object.values(gNode.flows)) {
                let set = referencedFlowInputs.get(target.node);
                if (!set) { set = new Set(); referencedFlowInputs.set(target.node, set); }
                set.add(target.socket);
            }
        }
    }

    // Track whether any node has authored metadata positions
    let hasMetadata = false;

    for (let i = 0; i < graph.nodes.length; i++) {
        const gNode = graph.nodes[i];
        const decl = graph.declarations[gNode.declaration];
        if (!decl) continue;

        if (gNode.metadata?.x !== undefined) hasMetadata = true;

        const def = getNodeDef(decl.op);
        const position = {
            x: (gNode.metadata?.x as number) ?? 0,
            y: (gNode.metadata?.y as number) ?? 0,
        };
        // A macro block carries its instance id so saving keeps it stable.
        const authoredMacroId = decl.op.startsWith(MACRO_OP_PREFIX) ? gNode.metadata?.[MACRO_ID_KEY] : undefined;
        const macroId = typeof authoredMacroId === 'string' ? authoredMacroId : undefined;

        // Extract config values
        const config: Record<string, unknown> = {};
        if (gNode.configuration) {
            for (const [key, cv] of Object.entries(gNode.configuration)) {
                config[key] = cv.value.length === 1 ? cv.value[0] : cv.value;
            }
        }

        // Extract inline literal values
        const inlineValues: Record<string, unknown[]> = {};
        if (gNode.values) {
            for (const [key, vi] of Object.entries(gNode.values)) {
                if ('value' in vi) {
                    inlineValues[key] = vi.value;
                }
            }
        }

        // Check for subgraph declaration
        const subRef = decl.extensions?.TRACE_subgraph;
        if (subRef && allGraphs) {
            const targetGraph = allGraphs[subRef.graph];
            // Build dynamic sockets from the subgraph's interface
            const dynamicSockets = targetGraph
                ? buildSubgraphSockets(targetGraph)
                : {
                    flowInputs: [...(referencedFlowInputs.get(i) ?? [])],
                    flowOutputs: gNode.flows ? Object.keys(gNode.flows) : [],
                    dataInputs: gNode.values
                        ? Object.keys(gNode.values).filter(k => {
                            const v = gNode.values![k];
                            return 'node' in v || 'value' in v;
                        })
                        : [],
                    dataOutputs: [...(referencedDataOutputs.get(i) ?? [])],
                };

            nodes.push({
                id: `node-${i}`,
                type: 'subgraph',
                position,
                data: {
                    op: decl.op,
                    label: decl.op,
                    graphIndex: i,
                    config,
                    inlineValues,
                    dynamicSockets,
                    subgraphIndex: subRef.graph,
                    subgraphName: decl.op,
                },
            });

            // Flow + value edges (same as normal nodes)
            pushEdges(gNode, i, edges);
            continue;
        }

        // Normal node (known or unknown op)
        let dynamicSockets: FlowNodeData['dynamicSockets'];
        if (!def) {
            dynamicSockets = {
                flowInputs: [...(referencedFlowInputs.get(i) ?? [])],
                flowOutputs: gNode.flows ? Object.keys(gNode.flows) : [],
                dataInputs: gNode.values
                    ? Object.keys(gNode.values).filter(k => {
                        const v = gNode.values![k];
                        return 'node' in v || 'value' in v;
                    })
                    : [],
                dataOutputs: [...(referencedDataOutputs.get(i) ?? [])],
            };
        } else {
            // Known op wired past its static def (e.g. a Sequence using socket
            // '2' — degraded macro expansions do): surface the extra flow
            // sockets so their edges have handles to land on.
            const extraFlowOutputs = Object.keys(gNode.flows ?? {}).filter(k => !def.flowOutputs.includes(k));
            const extraFlowInputs = [...(referencedFlowInputs.get(i) ?? [])].filter(k => !def.flowInputs.includes(k));
            if (extraFlowOutputs.length > 0 || extraFlowInputs.length > 0) {
                dynamicSockets = { flowInputs: extraFlowInputs, flowOutputs: extraFlowOutputs, dataInputs: [], dataOutputs: [] };
            }
        }

        nodes.push({
            id: `node-${i}`,
            type: def?.category ?? (dynamicSockets?.flowInputs.length ? 'execution' : 'data'),
            position,
            data: {
                op: decl.op,
                label: def?.label ?? decl.op,
                graphIndex: i,
                config,
                inlineValues,
                ...(dynamicSockets ? { dynamicSockets } : {}),
                ...(macroId !== undefined ? { macroId } : {}),
            },
        });

        pushEdges(gNode, i, edges);
    }

    // Auto-layout when no nodes have authored positions
    if (!hasMetadata && nodes.length > 0) {
        applyDagreLayout(nodes, edges);
    }

    return { nodes, edges };
}

/** Build dynamic sockets from a subgraph's interface declaration */
function buildSubgraphSockets(subgraph: GraphData): FlowNodeData['dynamicSockets'] {
    return {
        flowInputs: subgraph.inputFlowSockets ? Object.keys(subgraph.inputFlowSockets) : [],
        flowOutputs: subgraph.outputFlowSockets ? Object.keys(subgraph.outputFlowSockets) : [],
        dataInputs: subgraph.inputValueSockets ? Object.keys(subgraph.inputValueSockets) : [],
        dataOutputs: subgraph.outputValueSockets ? Object.keys(subgraph.outputValueSockets) : [],
    };
}

/** Push flow + value edges for a graph node */
function pushEdges(gNode: GraphNode, i: number, edges: Edge[]): void {
    if (gNode.flows) {
        for (const [socketName, target] of Object.entries(gNode.flows)) {
            edges.push({
                id: `flow-${i}-${socketName}-${target.node}`,
                source: `node-${i}`,
                sourceHandle: `flow-out-${socketName}`,
                target: `node-${target.node}`,
                targetHandle: `flow-in-${target.socket}`,
                type: 'default',
                animated: true,
                style: { stroke: '#f59e0b', strokeWidth: 2 },
            });
        }
    }
    if (gNode.values) {
        for (const [socketName, vi] of Object.entries(gNode.values)) {
            if ('node' in vi) {
                edges.push({
                    id: `value-${vi.node}-${vi.socket}-${i}-${socketName}`,
                    source: `node-${vi.node}`,
                    sourceHandle: `data-out-${vi.socket}`,
                    target: `node-${i}`,
                    targetHandle: `data-in-${socketName}`,
                    type: 'default',
                    style: { stroke: '#64748b', strokeWidth: 1, strokeDasharray: '5 3' },
                });
            }
        }
    }
}

// ============================================================================
// ReactFlow → Spec
// ============================================================================

/**
 * Convert ReactFlow nodes/edges back to a single GraphData.
 * Subgraph nodes are serialized with TRACE_subgraph extension on their declarations.
 * Returns the updated graph and the full graphs[] array (existing subgraph definitions preserved).
 */
export function reactFlowToGraph(
    nodes: Node<FlowNodeData>[],
    edges: Edge[],
    variables?: GraphVariable[],
    existingGraphs?: GraphData[],
    editingIndex?: number,
    _contextObject?: SceneObject | null,
): { graph: GraphData; allGraphs: GraphData[] } {
    // `trace/setState` is a single scoped node now (it fans out to the whole
    // state group at runtime — STATE_SCOPE_SPEC.md), so there is no group-state
    // bake pre-pass here. `_contextObject` is retained in the signature for
    // callers but no longer used during serialization.

    // Build declarations (deduplicated by op + subgraphIndex)
    const declKey = (op: string, subIdx?: number) => subIdx !== undefined ? `${op}::sg${subIdx}` : op;
    const keyToDecl = new Map<string, number>();
    const declarations: Declaration[] = [];

    for (const node of nodes) {
        const key = declKey(node.data.op, node.data.subgraphIndex);
        if (!keyToDecl.has(key)) {
            keyToDecl.set(key, declarations.length);
            const decl: Declaration = { op: node.data.op };
            if (node.data.subgraphIndex !== undefined) {
                decl.extensions = {
                    TRACE_subgraph: { graph: node.data.subgraphIndex },
                };
            }
            declarations.push(decl);
        }
    }

    // Build node index map: ReactFlow id → graph index
    const idToIndex = new Map<string, number>();
    nodes.forEach((n, i) => idToIndex.set(n.id, i));

    // Editor state is raised (see graphToReactFlow), so these are the graph's
    // own variables: a macro block's private variable is not among them, and
    // commit-time lowering re-creates it.
    const keptVariables: GraphVariable[] = variables ?? [];

    // Collect used canonical type indices, then build a compact types array.
    // Only value inputs and variables need type entries — config values are
    // identity/addressing params whose type index the runtime ignores.
    const usedCanonical = new Set<number>();

    for (const node of nodes) {
        if (node.data.inlineValues) {
            for (const val of Object.values(node.data.inlineValues)) {
                const v0 = val[0];
                usedCanonical.add(ALL_TYPE_SIGNATURES.indexOf(typeof v0 === 'boolean' ? 'bool' : typeof v0 === 'number' ? 'float' : 'string'));
            }
        }
    }
    for (const v of keptVariables) {
        usedCanonical.add(v.type);
    }

    // Build compact types array and canonical→compact index map
    const sortedUsed = [...usedCanonical].sort((a, b) => a - b);
    const types = sortedUsed.map(i => ({ signature: ALL_TYPE_SIGNATURES[i] }));
    const canonicalToCompact = new Map<number, number>();
    sortedUsed.forEach((canonical, compact) => canonicalToCompact.set(canonical, compact));

    // Build graph nodes
    const graphNodes: GraphNode[] = nodes.map(node => {
        const key = declKey(node.data.op, node.data.subgraphIndex);
        const declIndex = keyToDecl.get(key) ?? 0;

        // Configuration
        const configuration: Record<string, { type: number; value: unknown[] }> = {};
        const def = getNodeDef(node.data.op);
        if (def && node.data.config) {
            for (const field of def.configFields) {
                const val = node.data.config[field.name];
                if (val !== undefined && val !== '') {
                    const canonical = ALL_TYPE_SIGNATURES.indexOf(field.type === 'boolean' ? 'bool' : field.type === 'number' ? 'float' : 'string');
                    configuration[field.name] = {
                        type: canonicalToCompact.get(canonical) ?? 0,
                        value: Array.isArray(val) ? val : [val],
                    };
                }
            }
        }

        // Values (inline literals)
        const values: Record<string, { type: number; value: unknown[] } | { node: number; socket: string }> = {};
        if (node.data.inlineValues) {
            for (const [k, val] of Object.entries(node.data.inlineValues)) {
                const v0 = val[0];
                const canonical = ALL_TYPE_SIGNATURES.indexOf(typeof v0 === 'boolean' ? 'bool' : typeof v0 === 'number' ? 'float' : 'string');
                values[k] = { type: canonicalToCompact.get(canonical) ?? 0, value: val };
            }
        }

        // Value connections (from edges)
        const incomingValueEdges = edges.filter(
            e => e.target === node.id && e.targetHandle?.startsWith('data-in-')
        );
        for (const edge of incomingValueEdges) {
            const socketName = edge.targetHandle!.replace('data-in-', '');
            const sourceIndex = idToIndex.get(edge.source);
            const sourceSocket = edge.sourceHandle?.replace('data-out-', '') ?? 'value';
            if (sourceIndex !== undefined) {
                values[socketName] = { node: sourceIndex, socket: sourceSocket };
            }
        }

        // Flow connections
        const flows: Record<string, { node: number; socket: string }> = {};
        const outgoingFlowEdges = edges.filter(
            e => e.source === node.id && e.sourceHandle?.startsWith('flow-out-')
        );
        for (const edge of outgoingFlowEdges) {
            const socketName = edge.sourceHandle!.replace('flow-out-', '');
            const targetIndex = idToIndex.get(edge.target);
            const targetSocket = edge.targetHandle?.replace('flow-in-', '') ?? 'in';
            if (targetIndex !== undefined) {
                flows[socketName] = { node: targetIndex, socket: targetSocket };
            }
        }

        return {
            declaration: declIndex,
            ...(Object.keys(configuration).length > 0 ? { configuration } : {}),
            ...(Object.keys(values).length > 0 ? { values } : {}),
            ...(Object.keys(flows).length > 0 ? { flows } : {}),
            // A macro block re-emits its instance id so commit-time lowering
            // (trace-viewer lowerMacros) reuses it instead of minting a fresh
            // one — the stored expansion's identity stays stable.
            metadata: {
                x: node.position.x,
                y: node.position.y,
                ...(node.data.macroId !== undefined ? { macroId: node.data.macroId } : {}),
            },
        };
    });

    const graph: GraphData = {
        types,
        declarations,
        variables: keptVariables.map(v => ({ ...v, type: canonicalToCompact.get(v.type) ?? 0 })),
        events: buildEvents(graphNodes, declarations, existingGraphs?.[editingIndex ?? 0]?.events ?? []),
        nodes: graphNodes,
    };

    // Preserve subgraph interface sockets from the existing graph if present
    if (existingGraphs && editingIndex !== undefined) {
        const existing = existingGraphs[editingIndex];
        if (existing) {
            if (existing.inputFlowSockets) graph.inputFlowSockets = existing.inputFlowSockets;
            if (existing.outputFlowSockets) graph.outputFlowSockets = existing.outputFlowSockets;
            if (existing.inputValueSockets) graph.inputValueSockets = existing.inputValueSockets;
            if (existing.outputValueSockets) graph.outputValueSockets = existing.outputValueSockets;
        }
    }

    // Build updated allGraphs: replace the editing graph, keep subgraph definitions
    const allGraphs = existingGraphs ? [...existingGraphs] : [graph];
    const idx = editingIndex ?? 0;
    allGraphs[idx] = graph;

    return { graph, allGraphs };
}

/**
 * The graph's custom events on save. The editor has no event authoring, so
 * events reach it only through stored graphs (KHR imports, and expansions the
 * editor shows as plain nodes — a raised macro block's private event is not in
 * editor state; lowering re-creates it). Keep every EXTERNAL event (non-empty
 * id: its identity is its contract with the host), keep the internal events a
 * send/receive node still addresses, and drop the rest. A node addressing an
 * index past the existing events (a freshly expanded macro) gets a new
 * internal, value-less event. Send and receive nodes are remapped to the
 * compacted indices in place.
 */
function buildEvents(graphNodes: GraphNode[], declarations: Declaration[], existing: readonly GraphEvent[]): GraphEvent[] {
    const addressing: { value: unknown[] }[] = [];
    for (const gNode of graphNodes) {
        const op = declarations[gNode.declaration]?.op;
        const ref = gNode.configuration?.[MACRO_EVENT_CONFIG];
        if ((op === GraphOps.EVENT_SEND || op === GraphOps.EVENT_RECEIVE) && ref) addressing.push(ref);
    }
    const referenced = new Set<number>();
    for (const ref of addressing) {
        const index = ref.value[0];
        if (typeof index === 'number' && Number.isInteger(index) && index >= 0) referenced.add(index);
    }

    const events: GraphEvent[] = [];
    const indexMap = new Map<number, number>();
    const end = Math.max(existing.length, ...[...referenced].map(i => i + 1));
    for (let i = 0; i < end; i++) {
        const event = existing[i] as GraphEvent | undefined;
        if (event?.id || referenced.has(i)) {
            indexMap.set(i, events.length);
            events.push(event ? structuredClone(event) : { id: '' });
        }
    }
    for (const ref of addressing) {
        const mapped = typeof ref.value[0] === 'number' ? indexMap.get(ref.value[0]) : undefined;
        if (mapped !== undefined) ref.value = [mapped];
    }
    return events;
}

// ============================================================================
// Subgraph grouping — analyze selected nodes and create a subgraph
// ============================================================================

export interface GroupResult {
    /** Updated graphs array with the new subgraph appended */
    allGraphs: GraphData[];
    /** Updated ReactFlow nodes (selected nodes removed, subgraph node added) */
    nodes: Node<FlowNodeData>[];
    /** Updated ReactFlow edges (internal edges moved, external edges rewired) */
    edges: Edge[];
    /** Index of the new subgraph in allGraphs */
    newGraphIndex: number;
}

/**
 * Groups the selected ReactFlow nodes into a new subgraph definition.
 * Analyzes edges crossing the selection boundary to build the subgraph interface.
 */
export function groupIntoSubgraph(
    allNodes: Node<FlowNodeData>[],
    allEdges: Edge[],
    selectedNodeIds: Set<string>,
    existingGraphs: GraphData[],
    _variables: GraphVariable[],
    _editingIndex: number,
    subgraphName: string,
    contextObject?: SceneObject | null,
): GroupResult {
    const selectedNodes = allNodes.filter(n => selectedNodeIds.has(n.id));
    const remainingNodes = allNodes.filter(n => !selectedNodeIds.has(n.id));

    // Classify edges
    const internalEdges: Edge[] = [];
    const incomingEdges: Edge[] = []; // from outside → inside
    const outgoingEdges: Edge[] = []; // from inside → outside
    const externalEdges: Edge[] = [];  // neither end is selected

    console.log('[groupIntoSubgraph] selectedNodeIds:', [...selectedNodeIds]);
    console.log('[groupIntoSubgraph] allEdges:', allEdges.map(e => `${e.source} → ${e.target} (${e.sourceHandle} → ${e.targetHandle})`));

    for (const edge of allEdges) {
        const srcIn = selectedNodeIds.has(edge.source);
        const tgtIn = selectedNodeIds.has(edge.target);
        if (srcIn && tgtIn) internalEdges.push(edge);
        else if (!srcIn && tgtIn) incomingEdges.push(edge);
        else if (srcIn && !tgtIn) outgoingEdges.push(edge);
        else externalEdges.push(edge);
    }

    console.log('[groupIntoSubgraph] internal:', internalEdges.length, 'incoming:', incomingEdges.length, 'outgoing:', outgoingEdges.length, 'external:', externalEdges.length);

    // Build subgraph interface from boundary edges
    const inputFlowSockets: Record<string, { node: number; socket: string }> = {};
    const outputFlowSockets: Record<string, { node: number; socket: string }> = {};
    const inputValueSockets: Record<string, { type: number; node: number; socket: string }> = {};
    const outputValueSockets: Record<string, { node: number; socket: string; type: number }> = {};

    // Map selected node ReactFlow IDs to subgraph-local indices
    const selectedIdToLocal = new Map<string, number>();
    selectedNodes.forEach((n, i) => selectedIdToLocal.set(n.id, i));

    // Incoming flow edges → inputFlowSockets
    for (const edge of incomingEdges) {
        const isFlow = edge.targetHandle?.startsWith('flow-in-');
        const socketName = edge.targetHandle?.replace('flow-in-', '').replace('data-in-', '') ?? 'in';
        const localTarget = selectedIdToLocal.get(edge.target)!;

        if (isFlow) {
            const count = Object.keys(inputFlowSockets).length;
            const name = count === 0 ? 'in' : `in_${count}`;
            inputFlowSockets[name] = {
                node: localTarget,
                socket: socketName,
            };
        } else {
            const name = socketName;
            if (!inputValueSockets[name]) {
                inputValueSockets[name] = { type: 1, node: localTarget, socket: socketName }; // float default
            }
        }
    }

    // Outgoing flow edges → outputFlowSockets
    for (const edge of outgoingEdges) {
        const isFlow = edge.sourceHandle?.startsWith('flow-out-');
        const socketName = edge.sourceHandle?.replace('flow-out-', '').replace('data-out-', '') ?? 'out';
        const localSource = selectedIdToLocal.get(edge.source)!;

        if (isFlow) {
            const count = Object.keys(outputFlowSockets).length;
            const name = count === 0 ? 'out' : `out_${count}`;
            outputFlowSockets[name] = {
                node: localSource,
                socket: socketName,
            };
        } else {
            const name = socketName;
            if (!outputValueSockets[name]) {
                outputValueSockets[name] = {
                    node: localSource,
                    socket: socketName,
                    type: 1,
                };
            }
        }
    }

    // Convert selected nodes into a subgraph GraphData
    const { graph: subgraphData } = reactFlowToGraph(
        // Re-index nodes starting from 0
        selectedNodes.map((n, i) => ({ ...n, id: `node-${i}` })),
        // Re-index internal edges
        internalEdges.map(e => ({
            ...e,
            source: `node-${selectedIdToLocal.get(e.source)!}`,
            target: `node-${selectedIdToLocal.get(e.target)!}`,
        })),
        undefined,
        undefined,
        undefined,
        contextObject,
    );

    // If no boundary edges produced interface sockets, check whether the selected
    // nodes contain execution blocks (have flow inputs/outputs). If so, add default
    // in/out flow sockets pointing to the first and last execution nodes.
    if (Object.keys(inputFlowSockets).length === 0) {
        // Find the first selected node that has a flow input
        for (let i = 0; i < selectedNodes.length; i++) {
            const def = getNodeDef(selectedNodes[i].data.op);
            const dyn = selectedNodes[i].data.dynamicSockets;
            const hasFlowIn = (def?.flowInputs.length ?? 0) > 0 || (dyn?.flowInputs.length ?? 0) > 0;
            if (hasFlowIn) {
                inputFlowSockets['in'] = { node: i, socket: 'in' };
                break;
            }
        }
    }
    if (Object.keys(outputFlowSockets).length === 0) {
        // Find the last selected node that has a flow output
        for (let i = selectedNodes.length - 1; i >= 0; i--) {
            const def = getNodeDef(selectedNodes[i].data.op);
            const dyn = selectedNodes[i].data.dynamicSockets;
            const hasFlowOut = (def?.flowOutputs.length ?? 0) > 0 || (dyn?.flowOutputs.length ?? 0) > 0;
            if (hasFlowOut) {
                const outName = def?.flowOutputs[def.flowOutputs.length - 1] ?? dyn?.flowOutputs[dyn.flowOutputs.length - 1] ?? 'out';
                outputFlowSockets['out'] = { node: i, socket: outName };
                break;
            }
        }
    }

    // Add subgraph interface to the graph
    subgraphData.inputFlowSockets = Object.keys(inputFlowSockets).length > 0 ? inputFlowSockets : undefined;
    subgraphData.outputFlowSockets = Object.keys(outputFlowSockets).length > 0 ? outputFlowSockets : undefined;
    subgraphData.inputValueSockets = Object.keys(inputValueSockets).length > 0 ? inputValueSockets : undefined;
    subgraphData.outputValueSockets = Object.keys(outputValueSockets).length > 0 ? outputValueSockets : undefined;

    // Append the new subgraph to allGraphs
    const newGraphIndex = existingGraphs.length;
    const newAllGraphs = [...existingGraphs, subgraphData];

    // Create the subgraph node that replaces the selection
    const avgX = selectedNodes.reduce((sum, n) => sum + n.position.x, 0) / selectedNodes.length;
    const avgY = selectedNodes.reduce((sum, n) => sum + n.position.y, 0) / selectedNodes.length;

    const subgraphSockets = buildSubgraphSockets(subgraphData);

    const subgraphNode: Node<FlowNodeData> = {
        id: `node-sg-${newGraphIndex}`,
        type: 'subgraph',
        position: { x: avgX, y: avgY },
        data: {
            op: subgraphName,
            label: subgraphName,
            config: {},
            inlineValues: {},
            dynamicSockets: subgraphSockets,
            subgraphIndex: newGraphIndex,
            subgraphName,
        },
    };

    const newNodes = [...remainingNodes, subgraphNode];

    // Rewire boundary edges to point to the subgraph node
    const newEdges: Edge[] = [...externalEdges];

    // Incoming edges → rewire target to subgraph node
    for (const edge of incomingEdges) {
        const isFlow = edge.targetHandle?.startsWith('flow-in-');
        if (isFlow) {
            // Find which inputFlowSocket this maps to
            const socketName = edge.targetHandle?.replace('flow-in-', '') ?? 'in';
            const localTarget = selectedIdToLocal.get(edge.target)!;
            const ifName = Object.entries(inputFlowSockets).find(
                ([_, v]) => v.node === localTarget && v.socket === socketName
            )?.[0];
            if (ifName) {
                newEdges.push({
                    ...edge,
                    id: `${edge.id}-sg`,
                    target: subgraphNode.id,
                    targetHandle: `flow-in-${ifName}`,
                });
            }
        } else {
            const socketName = edge.targetHandle?.replace('data-in-', '') ?? '';
            newEdges.push({
                ...edge,
                id: `${edge.id}-sg`,
                target: subgraphNode.id,
                targetHandle: `data-in-${socketName}`,
            });
        }
    }

    // Outgoing edges → rewire source to subgraph node
    for (const edge of outgoingEdges) {
        const isFlow = edge.sourceHandle?.startsWith('flow-out-');
        if (isFlow) {
            const socketName = edge.sourceHandle?.replace('flow-out-', '') ?? 'out';
            const localSource = selectedIdToLocal.get(edge.source)!;
            const ofName = Object.entries(outputFlowSockets).find(
                ([_, v]) => v.node === localSource && v.socket === socketName
            )?.[0];
            if (ofName) {
                newEdges.push({
                    ...edge,
                    id: `${edge.id}-sg`,
                    source: subgraphNode.id,
                    sourceHandle: `flow-out-${ofName}`,
                });
            }
        } else {
            const socketName = edge.sourceHandle?.replace('data-out-', '') ?? '';
            newEdges.push({
                ...edge,
                id: `${edge.id}-sg`,
                source: subgraphNode.id,
                sourceHandle: `data-out-${socketName}`,
            });
        }
    }

    return {
        allGraphs: newAllGraphs,
        nodes: newNodes,
        edges: newEdges,
        newGraphIndex,
    };
}

// ============================================================================
// Inline subgraph — expand a subgraph node back into its constituent nodes
// ============================================================================

export function inlineSubgraph(
    allNodes: Node<FlowNodeData>[],
    allEdges: Edge[],
    subgraphNodeId: string,
    allGraphs: GraphData[],
    nextNodeId: number,
    variables: GraphVariable[] = [],
): {
    nodes: Node<FlowNodeData>[];
    edges: Edge[];
    allGraphs: GraphData[];
    nextNodeId: number;
    variables: GraphVariable[];
} {
    const subNode = allNodes.find(n => n.id === subgraphNodeId);
    if (!subNode || subNode.data.subgraphIndex === undefined) {
        return { nodes: allNodes, edges: allEdges, allGraphs, nextNodeId, variables };
    }

    const subgraphData = allGraphs[subNode.data.subgraphIndex];
    if (!subgraphData) {
        return { nodes: allNodes, edges: allEdges, allGraphs, nextNodeId, variables };
    }

    // A subgraph is a closure: at runtime each invocation gets its own
    // FlowGraphContext, so its variables are private (trace-viewer
    // GraphCompiler / FlowGraphSubgraphBlock). Inlining dissolves that scope,
    // so the body's variables have to be adopted into THIS graph — with fresh
    // names, since the parent may already have a `count` of its own.
    //
    // Variables resolve by INDEX, so every inlined variable/get and
    // variable/set must have its `variable` config remapped onto the adopted
    // copy. Without the remap it silently reads or writes whatever the parent
    // happens to hold at the body's index (trace-sandbox#194).
    const mergedVariables = [...variables];
    const takenNames = new Set(mergedVariables.map(v => v.name));
    const variableIndexMap = new Map<number, number>();
    // The body's type indices are its own; canonicalise them the same way a
    // graph's variables are canonicalised on load.
    remapVariablesToCanonical(subgraphData.variables, subgraphData.types).forEach((v, bodyIndex) => {
        let name = v.name;
        for (let n = 2; takenNames.has(name); n++) name = `${v.name}__${n}`;
        takenNames.add(name);
        variableIndexMap.set(bodyIndex, mergedVariables.length);
        mergedVariables.push({ ...v, name });
    });

    // Convert the subgraph's internal graph to ReactFlow nodes/edges
    const { nodes: innerNodes, edges: innerEdges } = graphToReactFlow(subgraphData, allGraphs);

    // Offset positions relative to the subgraph node's position
    const offsetX = subNode.position.x;
    const offsetY = subNode.position.y;

    // Ensure nextNodeId is high enough to avoid collisions with existing nodes
    for (const n of allNodes) {
        const num = parseInt(n.id.replace(/^node-(?:sg-)?/, ''), 10);
        if (!isNaN(num) && num >= nextNodeId) nextNodeId = num + 1;
    }

    // Remap inner node IDs to avoid collisions
    const innerIdMap = new Map<string, string>();
    const expandedNodes: Node<FlowNodeData>[] = [];
    for (const inner of innerNodes) {
        const newId = `node-${nextNodeId++}`;
        innerIdMap.set(inner.id, newId);
        const isVariableNode =
            inner.data.op === GraphOps.VARIABLE_GET || inner.data.op === GraphOps.VARIABLE_SET;
        const authoredIndex = Number(inner.data.config?.['variable']);
        const remapped = isVariableNode && variableIndexMap.has(authoredIndex);
        expandedNodes.push({
            ...inner,
            id: newId,
            position: {
                x: inner.position.x + offsetX,
                y: inner.position.y + offsetY,
            },
            ...(remapped
                ? {
                      data: {
                          ...inner.data,
                          config: { ...inner.data.config, variable: variableIndexMap.get(authoredIndex) },
                      },
                  }
                : {}),
        });
    }

    // Remap inner edges
    const expandedEdges: Edge[] = innerEdges.map(e => ({
        ...e,
        id: `${e.id}-exp-${nextNodeId}`,
        source: innerIdMap.get(e.source) ?? e.source,
        target: innerIdMap.get(e.target) ?? e.target,
    }));

    // Build reverse mapping: subgraph interface socket → expanded node ID + socket
    const flowInMap = new Map<string, { nodeId: string; socket: string }>();
    const flowOutMap = new Map<string, { nodeId: string; socket: string }>();
    const valueInMap = new Map<string, { nodeId: string; socket: string }>();
    const valueOutMap = new Map<string, { nodeId: string; socket: string }>();

    if (subgraphData.inputFlowSockets) {
        for (const [name, ref] of Object.entries(subgraphData.inputFlowSockets)) {
            flowInMap.set(name, {
                nodeId: innerIdMap.get(`node-${ref.node}`) ?? '',
                socket: ref.socket,
            });
        }
    }
    if (subgraphData.outputFlowSockets) {
        for (const [name, ref] of Object.entries(subgraphData.outputFlowSockets)) {
            flowOutMap.set(name, {
                nodeId: innerIdMap.get(`node-${ref.node}`) ?? '',
                socket: ref.socket,
            });
        }
    }
    if (subgraphData.inputValueSockets) {
        for (const [name, decl] of Object.entries(subgraphData.inputValueSockets)) {
            // Prefer stored node/socket mapping (set by groupIntoSubgraph)
            if (decl.node !== undefined) {
                const mappedId = innerIdMap.get(`node-${decl.node}`);
                if (mappedId) {
                    valueInMap.set(name, { nodeId: mappedId, socket: decl.socket ?? name });
                    continue;
                }
            }
            // Fallback: scan expanded nodes for matching data input name
            for (const inner of expandedNodes) {
                const dyn = inner.data.dynamicSockets;
                const def = getNodeDef(inner.data.op);
                const inputs = def?.dataInputs.map(s => s.name) ?? dyn?.dataInputs ?? [];
                if (inputs.includes(name)) {
                    valueInMap.set(name, { nodeId: inner.id, socket: name });
                    break;
                }
            }
        }
    }
    if (subgraphData.outputValueSockets) {
        for (const [name, ref] of Object.entries(subgraphData.outputValueSockets)) {
            valueOutMap.set(name, {
                nodeId: innerIdMap.get(`node-${ref.node}`) ?? '',
                socket: ref.socket,
            });
        }
    }

    // Remove the subgraph node, add expanded nodes
    const newNodes = allNodes.filter(n => n.id !== subgraphNodeId).concat(expandedNodes);

    // Rewire edges that connected to the subgraph node
    const newEdges: Edge[] = [...expandedEdges];
    for (const edge of allEdges) {
        if (edge.source === subgraphNodeId) {
            // Outgoing from subgraph → rewire source to internal exit node
            const isFlow = edge.sourceHandle?.startsWith('flow-out-');
            const socketName = edge.sourceHandle?.replace('flow-out-', '').replace('data-out-', '') ?? '';
            const mapping = isFlow ? flowOutMap.get(socketName) : valueOutMap.get(socketName);
            if (mapping) {
                newEdges.push({
                    ...edge,
                    id: `${edge.id}-exp`,
                    source: mapping.nodeId,
                    sourceHandle: isFlow ? `flow-out-${mapping.socket}` : `data-out-${mapping.socket}`,
                });
            }
        } else if (edge.target === subgraphNodeId) {
            // Incoming to subgraph → rewire target to internal entry node
            const isFlow = edge.targetHandle?.startsWith('flow-in-');
            const socketName = edge.targetHandle?.replace('flow-in-', '').replace('data-in-', '') ?? '';
            const mapping = isFlow ? flowInMap.get(socketName) : valueInMap.get(socketName);
            if (mapping) {
                newEdges.push({
                    ...edge,
                    id: `${edge.id}-exp`,
                    target: mapping.nodeId,
                    targetHandle: isFlow ? `flow-in-${mapping.socket}` : `data-in-${mapping.socket}`,
                });
            }
        } else {
            // Edge doesn't touch the subgraph node — keep as-is
            newEdges.push(edge);
        }
    }

    return { nodes: newNodes, edges: newEdges, allGraphs, nextNodeId, variables: mergedVariables };
}

// ============================================================================
// Auto-layout via dagre
// ============================================================================

const NODE_WIDTH = 180;
const NODE_HEIGHT = 80;

/** Lay `nodes` out top-to-bottom along `edges` (mutates positions). */
export function applyDagreLayout(nodes: Node<FlowNodeData>[], edges: Edge[]): void {
    const g = new dagre.graphlib.Graph<GraphLabel, NodeLabel, EdgeLabel>();
    g.setDefaultEdgeLabel(() => ({}));
    g.setGraph({ rankdir: 'TB', nodesep: 60, ranksep: 80, edgesep: 20 });

    for (const node of nodes) {
        g.setNode(node.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
    }

    for (const edge of edges) {
        g.setEdge(edge.source, edge.target);
    }

    dagre.layout(g);

    for (const node of nodes) {
        const pos = g.node(node.id) as { x: number; y: number } | undefined;
        if (pos) {
            node.position = {
                x: pos.x - NODE_WIDTH / 2,
                y: pos.y - NODE_HEIGHT / 2,
            };
        }
    }
}

// ============================================================================
// Update subgraph interface sockets from port edges
// ============================================================================

/**
 * Extract interface socket updates from port edges.
 * When a user rewires a port edge to a different internal node, this updates
 * the subgraph's interface sockets to reflect the new mapping.
 */
export function updateInterfaceFromPortEdges(graph: GraphData, portEdges: Edge[]): void {
    const idToNode = new Map<string, number>();
    for (let i = 0; i < graph.nodes.length; i++) {
        idToNode.set(`node-${i}`, i);
    }

    const inputFlow: Record<string, { node: number; socket: string }> = {};
    const outputFlow: Record<string, { node: number; socket: string }> = {};
    const outputValue: Record<string, { node: number; socket: string; type: number }> = {};

    for (const edge of portEdges) {
        if (edge.source === '__port-input') {
            const isFlow = edge.sourceHandle?.startsWith('flow-out-');
            if (isFlow) {
                const socketName = edge.sourceHandle!.replace('flow-out-', '');
                const targetIdx = idToNode.get(edge.target);
                const targetSocket = edge.targetHandle?.replace('flow-in-', '') ?? 'in';
                if (targetIdx !== undefined) {
                    inputFlow[socketName] = { node: targetIdx, socket: targetSocket };
                }
            }
        } else if (edge.target === '__port-output') {
            const isFlow = edge.targetHandle?.startsWith('flow-in-');
            if (isFlow) {
                const socketName = edge.targetHandle!.replace('flow-in-', '');
                const sourceIdx = idToNode.get(edge.source);
                const sourceSocket = edge.sourceHandle?.replace('flow-out-', '') ?? 'out';
                if (sourceIdx !== undefined) {
                    outputFlow[socketName] = { node: sourceIdx, socket: sourceSocket };
                }
            } else {
                const socketName = edge.targetHandle?.replace('data-in-', '') ?? '';
                const sourceIdx = idToNode.get(edge.source);
                const sourceSocket = edge.sourceHandle?.replace('data-out-', '') ?? 'value';
                if (sourceIdx !== undefined) {
                    outputValue[socketName] = { node: sourceIdx, socket: sourceSocket, type: 0 };
                }
            }
        }
    }

    graph.inputFlowSockets = Object.keys(inputFlow).length > 0 ? inputFlow : graph.inputFlowSockets;
    graph.outputFlowSockets = Object.keys(outputFlow).length > 0 ? outputFlow : graph.outputFlowSockets;
    graph.outputValueSockets = Object.keys(outputValue).length > 0 ? outputValue : graph.outputValueSockets;
}
